'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { BRAND_LOGO_LOGIN_SRC } from '@/lib/brand-assets';
import { checkActiveSuperAdmin, isActiveSuperAdmin } from '@/lib/auth/super-admin';
import { configureAuthStorage, getSupabaseBrowserClient } from '@/lib/supabase/browser-client';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

function formatAuthMessage(message: string): string {
  if (/invalid login credentials/i.test(message)) {
    return 'Invalid email or password.';
  }
  return message;
}

export default function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const supabase = getSupabaseBrowserClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled || !session?.user) return;
        const ok = await isActiveSuperAdmin(supabase, session.user.id);
        if (ok) router.replace('/lead-management');
      } catch {
        /* env not configured in dev */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [router]);

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    clearErrors('root');

    try {
      configureAuthStorage(data.rememberMe);
      const supabase = getSupabaseBrowserClient();

      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: data.email.trim(),
        password: data.password,
      });

      if (authError) {
        setError('root', { message: formatAuthMessage(authError.message) });
        return;
      }

      if (!authData.user) {
        setError('root', { message: 'Sign-in failed. Try again.' });
        return;
      }

      /* Ensure the access token is on the client before PostgREST (RLS) runs; avoids false "not super admin" right after sign-in. */
      if (!authData.session?.access_token) {
        await supabase.auth.signOut();
        setError('root', {
          message:
            'Sign-in did not return a session token. Check Supabase Auth (e.g. email confirmation) and that this app uses the same project URL/key as your database.',
        });
        return;
      }

      const {
        data: { session },
        error: sessionAfterSignInError,
      } = await supabase.auth.getSession();
      if (sessionAfterSignInError || !session?.user?.id) {
        await supabase.auth.signOut();
        setError('root', {
          message: sessionAfterSignInError?.message ?? 'Session did not initialize after sign-in. Try again.',
        });
        return;
      }

      const superCheck = await checkActiveSuperAdmin(supabase, session.user.id);
      if (!superCheck.ok) {
        await supabase.auth.signOut();
        if (superCheck.kind === 'request_error') {
          setError('root', {
            message: `Could not verify super admin (check browser console and Supabase project URL/key). ${superCheck.message ?? ''}`.trim(),
          });
          return;
        }
        setError('root', {
          message:
            'This account is not a platform super admin. Ensure migration 51 is applied (function public.is_super_admin) and this user has an Active row in super_admins.',
        });
        return;
      }

      toast.success('Signed in successfully');
      window.location.replace('/lead-management');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Something went wrong. Check Supabase environment variables.';
      setError('root', { message: msg });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex flex-col items-center justify-center px-4 sm:px-6 py-12 overflow-hidden bg-login-canvas">
      {/* Dot pattern — top-right splash accent */}
      <div className="pointer-events-none absolute inset-0 bg-login-halftone opacity-90" aria-hidden />
      {/* Structured grid — fades toward edges */}
      <div className="pointer-events-none absolute inset-0 bg-login-mesh opacity-80" aria-hidden />
      {/* Soft brand arc — top */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-28 left-1/2 h-64 w-[min(150%,60rem)] -translate-x-1/2 rounded-[100%] bg-[hsl(var(--primary)/0.09)] blur-3xl"
      />
      {/* Cherry bloom — top right (organic blob) */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-[2%] h-[22rem] w-[22rem] rounded-[45%] bg-[hsl(var(--primary)/0.12)] blur-3xl sm:h-[26rem] sm:w-[26rem]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 top-[12%] h-48 w-48 rounded-full bg-[hsl(var(--primary-light)/0.2)] blur-2xl sm:h-56 sm:w-56"
      />
      {/* Cherry depth — bottom left */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-20 bottom-[8%] h-[20rem] w-[20rem] rounded-full bg-[hsl(var(--primary-dark)/0.1)] blur-3xl"
      />
      {/* Center glow behind card — soft pink */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1/2 h-[min(115vw,44rem)] w-[min(115vw,44rem)] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[hsl(var(--primary-soft)/0.85)] blur-3xl"
      />
      {/* Flowing waves — bottom (splash-style cherry gradient) */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 z-[1] h-[38%] min-h-[180px] max-h-[340px]"
        aria-hidden
      >
        <svg
          className="absolute bottom-0 h-full w-full"
          viewBox="0 0 1440 300"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="loginWaveDeep" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(351 85% 45%)" stopOpacity="0.38" />
              <stop offset="45%" stopColor="hsl(352 73% 64%)" stopOpacity="0.18" />
              <stop offset="100%" stopColor="hsl(354 100% 97%)" stopOpacity="0.06" />
            </linearGradient>
            <linearGradient id="loginWaveSoft" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="hsl(352 83% 38%)" stopOpacity="0.28" />
              <stop offset="70%" stopColor="hsl(352 73% 64%)" stopOpacity="0.12" />
              <stop offset="100%" stopColor="hsl(352 78% 95%)" stopOpacity="0.04" />
            </linearGradient>
          </defs>
          <path
            fill="url(#loginWaveDeep)"
            d="M0,100 C180,180 360,40 540,90 C720,140 900,20 1080,80 C1260,140 1380,100 1440,120 L1440,320 L0,320 Z"
          />
          <path
            fill="url(#loginWaveSoft)"
            d="M0,160 C220,240 400,120 640,150 C880,180 1100,100 1440,170 L1440,320 L0,320 Z"
            opacity="0.88"
          />
        </svg>
      </div>
      {/* Diagonal sheen — cherry only */}
      <div className="pointer-events-none absolute inset-0 z-[1] bg-login-sheen opacity-[0.55]" aria-hidden />

      <div className="relative z-10 w-full max-w-[420px] animate-fade-up">
        {/* Card */}
        <div className="rounded-3xl bg-white/85 backdrop-blur-md border border-[hsl(var(--border))] shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_24px_48px_-24px_rgba(15,23,42,0.12),0_0_0_1px_rgba(15,23,42,0.03)] overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-[hsl(var(--primary-light))] via-[hsl(var(--primary))] to-[hsl(var(--primary-dark))]" aria-hidden />
          <div className="p-7 sm:p-8">
            {/* Brand — compact wordmark inside card */}
            <div className="flex flex-col items-center text-center mb-5">
              <div className="relative max-w-[min(100%,148px)] rounded-xl overflow-hidden shadow-[0_8px_28px_-12px_hsl(var(--primary)/0.3)] ring-1 ring-[hsl(var(--primary)/0.1)]">
                <AppImage
                  src={BRAND_LOGO_LOGIN_SRC}
                  alt="POSTrack"
                  width={480}
                  height={520}
                  className="w-full h-auto object-contain select-none"
                  priority
                  unoptimized
                />
              </div>
              <p className="mt-2.5 text-[10px] font-700 tracking-[0.2em] uppercase text-[hsl(var(--primary))]">
                Admin Panel
              </p>
            </div>

            <div className="mb-6">
              <h2 className="text-xl font-800 text-[hsl(var(--foreground))] tracking-tight">Sign in</h2>
              <p className="mt-1 text-[13.5px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                Super Admin access only. Use your verified credentials.
              </p>
            </div>

            <form
              method="post"
              noValidate
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit(onSubmit)(e);
              }}
            >
              <div>
                <label htmlFor="email" className="block text-[12.5px] font-700 text-[hsl(var(--foreground))] mb-1.5">
                  Email address
                </label>
                <div className="relative">
                  <Icon
                    name="EnvelopeIcon"
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
                  />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@postrack.in"
                    className={`
                      w-full h-11 pl-10 pr-3.5 rounded-xl border text-[14px] text-[hsl(var(--foreground))]
                      bg-[hsl(0_0%_100%)] placeholder:text-[hsl(var(--muted-foreground))]
                      focus:outline-none focus:ring-4 focus:ring-[hsl(var(--primary)/0.12)] focus:border-[hsl(var(--primary))]
                      transition-all duration-200
                      ${errors.email ? 'border-red-400 focus:ring-red-400/25 focus:border-red-400' : 'border-[hsl(var(--border))]'}
                    `}
                    {...register('email', {
                      required: 'Email address is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1.5 text-[12px] text-red-600 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={13} className="flex-shrink-0" variant="solid" />
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-[12.5px] font-700 text-[hsl(var(--foreground))] mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Icon
                    name="LockClosedIcon"
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
                  />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className={`
                      w-full h-11 pl-10 pr-10 rounded-xl border text-[14px] text-[hsl(var(--foreground))]
                      bg-[hsl(0_0%_100%)] placeholder:text-[hsl(var(--muted-foreground))]
                      focus:outline-none focus:ring-4 focus:ring-[hsl(var(--primary)/0.12)] focus:border-[hsl(var(--primary))]
                      transition-all duration-200
                      ${errors.password ? 'border-red-400 focus:ring-red-400/25 focus:border-red-400' : 'border-[hsl(var(--border))]'}
                    `}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: { value: 6, message: 'Password must be at least 6 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors duration-150 p-0.5"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={17} />
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-[12px] text-red-600 flex items-center gap-1">
                    <Icon name="ExclamationCircleIcon" size={13} className="flex-shrink-0" variant="solid" />
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 pt-0.5">
                <label htmlFor="rememberMe" className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    className="w-4 h-4 rounded border-[hsl(var(--border))] text-[hsl(var(--primary))] accent-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)/0.25)] focus:ring-offset-0 cursor-pointer"
                    {...register('rememberMe')}
                  />
                  <span className="text-[13px] text-[hsl(var(--muted-foreground))]">
                    Keep me signed in for 30 days
                  </span>
                </label>
              </div>

              {errors.root && (
                <div className="flex items-start gap-2.5 p-3.5 bg-[hsl(var(--primary-tint)/0.5)] border border-[hsl(var(--primary)/0.2)] rounded-xl">
                  <Icon name="ExclamationTriangleIcon" size={16} className="text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" variant="solid" />
                  <p className="text-[13px] text-[hsl(var(--primary-dark))]">{errors.root.message}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 rounded-xl font-700 text-[14px] text-white bg-cherry-gradient shadow-cherry
                           hover:shadow-cherry-glow active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2
                           disabled:opacity-70 disabled:cursor-not-allowed disabled:active:scale-100 disabled:hover:shadow-cherry"
              >
                {isLoading ? (
                  <>
                    <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                    <span>Signing in…</span>
                  </>
                ) : (
                  <>
                    Sign in to POSTrack
                    <Icon name="ArrowRightIcon" size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
